/**
 * Apple Ads → 红线规则配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 镜像 pages/huawei/RedlineConfigPage.tsx 骨架，按需求裁剪（PR-AT-20260911-002）：
 *   - 仅「投放时间」1 个区块（华为另有 出价/监测链接/回传异常监控）；
 *   - 投放时间下按 parent_id 分「工作日」「周末与节假日」两个配置区域，交互与
 *     华为完全相同（48 半小时块拖选 + 豁免列表，见 AppleLaunchTimeRangeEditor）；
 *   - Apple Ads 不区分 搜索/鸿蒙 → child_id 固定「默认」（主键占位，前端不展示）；
 *   - 豁免维度为关键词（exemptKeywords）而非华为的任务 ID（exemptTaskIds）；
 *   - 「周末与节假日」组标签旁有「不投放」勾选框（需求微调 20260911）：勾选即
 *     startHMs/endHMs 置 00:00，取消勾选恢复勾选前时段（豁免关键词不受影响）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/apple-asa/redline-config 拉取全部配置行。
 *   - 保存：PUT /api/v1/apple-asa/redline-config，body = { rows }（全量行）。
 *     create_user 保留首次、modify_user 每次更新（后端 SQL 层保证）。
 *   - 空表：显示「初始化默认配置」按钮 → POST /seed 种入 2 行初版配置
 *     （工作日 09:00-24:00 / 周末与节假日 10:00-18:00，豁免关键词为空）。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type AppleRedlineConfigItem,
  type AppleRedlineConfigRecord,
  getAppleRedlineConfig,
  saveAppleRedlineConfig,
  seedAppleRedlineConfig,
} from "../../services/apple/redlineConfig";
import { AppleLaunchTimeRangeEditor } from "../../components/appleRedlineConfig/AppleLaunchTimeRangeEditor";
import { pushToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";

/** field 常量（与后端 redline_config_service 校验规则一致）。 */
const FIELD_LAUNCH_TIME = "投放时间";

/** parent_id 展示顺序（工作日在前；未知 parent 兜底排后，按 DB 顺序）。 */
const PARENT_ORDER: string[] = ["工作日", "周末与节假日"];

/** 「不投放」勾选框仅挂在 周末与节假日 组（需求微调 20260911）。 */
const PARENT_WEEKEND = "周末与节假日";

/** 取消勾选「不投放」时的兜底时段（与 seed 默认一致；勾选前快照丢失时使用）。 */
const WEEKEND_DEFAULT_RANGE = { start: "10:00", end: "18:00" };

/** 行签名（脏检查用）：三元组 + ope_value。 */
function sig(it: AppleRedlineConfigItem): string {
  return `${it.field}|${it.parent_id}|${it.child_id}|${it.ope_value}`;
}

/** 含审计列的记录 → 可编辑行。 */
function toItem(r: AppleRedlineConfigRecord): AppleRedlineConfigItem {
  return {
    field: r.field,
    parent_id: r.parent_id,
    child_id: r.child_id,
    ope_value: r.ope_value ?? "",
  };
}

/** 行单元 key（与表主键一致）。 */
function rowKey(row: AppleRedlineConfigItem): string {
  return `${row.field}|${row.parent_id}|${row.child_id}`;
}

/** 投放时间 ope_value → 三键（与 AppleLaunchTimeRangeEditor.parseValue 同口径，页面侧
 *  「不投放」勾选框读写复用；非法 JSON 降级为未选择空值）。 */
function readLaunchTime(value: string): {
  startHMs: string;
  endHMs: string;
  exemptKeywords: string[];
} {
  try {
    const obj: unknown = JSON.parse(value);
    if (obj !== null && typeof obj === "object") {
      const rec = obj as Record<string, unknown>;
      return {
        startHMs: typeof rec.startHMs === "string" ? rec.startHMs : "00:00",
        endHMs: typeof rec.endHMs === "string" ? rec.endHMs : "00:00",
        exemptKeywords: Array.isArray(rec.exemptKeywords)
          ? rec.exemptKeywords.filter((x): x is string => typeof x === "string")
          : [],
      };
    }
  } catch {
    // 非法 JSON：降级为未选择 + 空豁免关键词
  }
  return { startHMs: "00:00", endHMs: "00:00", exemptKeywords: [] };
}

interface ParentGroup {
  parentId: string;
  rows: { row: AppleRedlineConfigItem; index: number }[];
}

export default function RedlineConfigPage() {
  const { canEdit } = useAuth();
  const canEditPage = canEdit(PAGE_KEYS.APPLE_REDLINE);
  const [items, setItems] = useState<AppleRedlineConfigItem[]>([]);
  const [loaded, setLoaded] = useState<AppleRedlineConfigRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  /**
   * 「不投放」勾选前该行的时段快照（行 key → start/end），取消勾选时恢复；
   * 仅内存态（保存后重载页面丢失），丢失时取消勾选兜底恢复 WEEKEND_DEFAULT_RANGE。
   */
  const savedRangesRef = useRef<Record<string, { start: string; end: string }>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getAppleRedlineConfig();
      setItems(rows.map(toItem));
      setLoaded(rows);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取 Apple Ads 红线配置失败";
      pushToast(msg, "error");
      setItems([]);
      setLoaded([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    return items.map(sig).join("\n") !== loaded.map(sig).join("\n");
  }, [items, loaded]);

  function updateValue(index: number, ope_value: string) {
    setItems((prev) => prev.map((it, j) => (j === index ? { ...it, ope_value } : it)));
  }

  /**
   * 「不投放」勾选状态由 ope_value 派生：startHMs 与 endHMs 均为 00:00 即视为
   * 全天不投放（勾选即把两键置 00:00，豁免关键词原样保留）。
   */
  function isNoDelivery(row: AppleRedlineConfigItem): boolean {
    const v = readLaunchTime(row.ope_value);
    return v.startHMs === "00:00" && v.endHMs === "00:00";
  }

  /** 勾选「不投放」：两键置 00:00（记录勾选前时段快照）；取消勾选：恢复快照时段。 */
  function toggleNoDelivery(row: AppleRedlineConfigItem, index: number, next: boolean) {
    const key = rowKey(row);
    const v = readLaunchTime(row.ope_value);
    if (next) {
      // 快照仅在首次勾选时记录；当前已是 00:00/00:00（无可恢复内容）时兜底 seed 默认
      const snapshot =
        v.startHMs === "00:00" && v.endHMs === "00:00"
          ? WEEKEND_DEFAULT_RANGE
          : { start: v.startHMs, end: v.endHMs };
      savedRangesRef.current[key] = savedRangesRef.current[key] ?? snapshot;
      updateValue(
        index,
        JSON.stringify({
          startHMs: "00:00",
          endHMs: "00:00",
          exemptKeywords: v.exemptKeywords,
        }),
      );
    } else {
      const saved = savedRangesRef.current[key] ?? WEEKEND_DEFAULT_RANGE;
      updateValue(
        index,
        JSON.stringify({
          startHMs: saved.start,
          endHMs: saved.end,
          exemptKeywords: v.exemptKeywords,
        }),
      );
    }
  }

  async function onSeed() {
    setSeeding(true);
    try {
      const n = await seedAppleRedlineConfig();
      pushToast(
        n > 0 ? `已初始化 ${n} 行初版配置` : "配置已存在，未覆盖（仅空表生效）",
        n > 0 ? "success" : "info",
      );
      await reload();
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "初始化失败", "error");
    } finally {
      setSeeding(false);
    }
  }

  async function onSave() {
    setSaving(true);
    try {
      const rows = await saveAppleRedlineConfig({ rows: items });
      setItems(rows.map(toItem));
      setLoaded(rows);
      pushToast("保存成功", "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存失败",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  /** 按 parent_id 分组（组内保持 DB 排序），parent 按 PARENT_ORDER 排。 */
  const parents: ParentGroup[] = useMemo(() => {
    if (!loaded) return [];
    const parentMap = new Map<string, { row: AppleRedlineConfigItem; index: number }[]>();
    items.forEach((row, index) => {
      let rows = parentMap.get(row.parent_id);
      if (!rows) {
        rows = [];
        parentMap.set(row.parent_id, rows);
      }
      rows.push({ row, index });
    });
    const parentIds = [...parentMap.keys()].sort((a, b) => {
      const ia = PARENT_ORDER.indexOf(a);
      const ib = PARENT_ORDER.indexOf(b);
      return (ia === -1 ? PARENT_ORDER.length : ia) - (ib === -1 ? PARENT_ORDER.length : ib);
    });
    return parentIds.map((parentId) => ({ parentId, rows: parentMap.get(parentId) ?? [] }));
  }, [items, loaded]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            红线规则配置
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            Apple Ads 红线规则（投放时间：工作日 / 周末与节假日 + 豁免关键词），保存到
            auto_apple_asa_redline_config；create_user 保留首次、modify_user 每次更新。
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loaded && loaded.length > 0 ? (
            <span className="leo-tag is-info">已配置 · {loaded.length} 行</span>
          ) : (
            <span className="leo-tag is-warning">尚未配置</span>
          )}
          {dirty ? <span className="leo-tag is-warning">有未保存的修改</span> : null}
        </div>
      </div>

      {!canEditPage ? (
        <div className="mb-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
          当前账号为只读，无法编辑该页面配置。
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="leo-card rounded-xl p-8 text-center">
          <div className="mb-3 text-[12.5px] text-text-muted">
            尚无 Apple Ads 红线规则配置，可一键初始化 2 行初版配置（投放时间：
            工作日 09:00-24:00 / 周末与节假日 10:00-18:00）。
          </div>
          <button
            type="button"
            onClick={onSeed}
            disabled={seeding || !canEditPage}
            className="leo-cta-primary w-auto px-7"
          >
            {seeding ? "初始化中..." : "初始化默认配置"}
          </button>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[12.5px] text-text-muted">
              共 <span className="text-text-emphasis">{items.length}</span> 行配置，主键
              field + parent_id + child_id 只读，仅 ope_value 可编辑
            </div>
          </div>

          <div className="space-y-4">
            {/* 投放时间区块（当前唯一 field；Apple Ads 无 搜索/鸿蒙 维度，
                child_id 固定「默认」为占位主键，前端不展示子项标签） */}
            <div className="rounded-xl border border-line-strong bg-da-card p-5 shadow-card">
              <div className="mb-4 text-[15.2px] font-bold text-text-emphasis">
                {FIELD_LAUNCH_TIME}
              </div>

              <div className="space-y-5">
                {parents.map((parent) => (
                  <div key={parent.parentId}>
                    <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="text-[12.5px] font-semibold text-text-strong">
                        {parent.parentId}
                      </span>
                      {/* 周末与节假日 专属「不投放」勾选框：勾选即 startHMs/endHMs 置 00:00，
                          取消勾选恢复勾选前时段（豁免关键词均不受影响） */}
                      {parent.parentId === PARENT_WEEKEND
                        ? parent.rows.map(({ row, index }) => (
                            <label
                              key={rowKey(row)}
                              title="勾选后周末与节假日全天不投放（startHMs/endHMs 置 00:00）"
                              className={`flex items-center gap-1.5 text-[12px] ${
                                canEditPage
                                  ? "cursor-pointer text-text-body"
                                  : "cursor-not-allowed text-text-muted"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="accent-[#ff6b35]"
                                checked={isNoDelivery(row)}
                                onChange={(e) => toggleNoDelivery(row, index, e.target.checked)}
                                disabled={!canEditPage}
                              />
                              不投放
                            </label>
                          ))
                        : null}
                    </div>
                    {/* child_id 固定「默认」且每个 parent 仅一行，编辑器直接整宽展示 */}
                    {parent.rows.map(({ row, index }) => (
                      <AppleLaunchTimeRangeEditor
                        key={rowKey(row)}
                        value={row.ope_value}
                        onChange={(v) => updateValue(index, v)}
                        disabled={!canEditPage}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-7 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => reload()}
              disabled={saving}
              className="leo-btn-ghost"
            >
              重新加载
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !canEditPage}
              className="leo-cta-primary w-auto px-7"
            >
              {saving ? "保存中..." : dirty ? "保存配置" : "已保存"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
